import { useState, useMemo, useEffect, useCallback } from 'react';
import { fetchData, downloadUrl, intl } from 'dmsbasereact';

const DEFAULT_FILTERS = {
  startDate: null,
  endDate: null,
  branch: "",
  pharmacist: "",
  batch: "",
  granularity: "week",
};

const DEFAULT_SUMMARY = {
  total_prescribed: 0,
  total_dispensed: 0,
  completion_rate: 0,
  total_dispensations: 0,
  divergent_dispensations: 0,
  divergence_rate: 0,
  top_branches: [],
  top_pharmacists: [],
};

const DEFAULT_CHART_RAW = {
  granularity: "week",
  points: [],
};

/**
 * Get granularity options for select component
 * @returns {Array} Array of {value, label} objects
 */
const getGranularityOptions = () => [
    { label: intl.get("DispensationInsights.Granularity.Week").d("Week"), value: "week" },
    { label: intl.get("DispensationInsights.Granularity.Month").d("Month"), value: "month" },
];

const CHART_OPTIONS = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: 'top',
        },
    },
    scales: {
        x: {
            beginAtZero: true,
        },
        y: {
            beginAtZero: true,
        },
    },
};

const buildPreFilters = (state) => {
    const nextFilters = [];
    if (state.startDate) {
        nextFilters.push({ name: "start_date", value: state.startDate });
    }
    if (state.endDate) {
        nextFilters.push({ name: "end_date", value: state.endDate });
    }
    if (state.branch) {
        nextFilters.push({ name: "organization_branch_pk", value: state.branch });
    }
    if (state.pharmacist) {
        nextFilters.push({ name: "pharmacist_pk", value: state.pharmacist });
    }
    if (state.batch) {
        nextFilters.push({ name: "batch_pk", value: state.batch });
    }
    return nextFilters;
};

const transformChartData = (rawData) => {
    const points = rawData?.points || [];

    const labels = points.map(point => point.label || '');

    const datasets = [
        {
            label: intl.get("DispensationInsights.Chart.DispensedBoxes").d("Dispensed Boxes"),
            data: points.map(point => point.dispensed_boxes || 0),
            backgroundColor: 'rgba(54, 162, 235, 0.5)',
            borderColor: 'rgb(54, 162, 235)',
            borderWidth: 1,
        },
        {
            label: intl.get("DispensationInsights.Chart.DispensedStrength").d("Dispensed Strength"),
            data: points.map(point => point.dispensed_strength || 0),
            backgroundColor: 'rgba(255, 99, 132, 0.5)',
            borderColor: 'rgb(255, 99, 132)',
            borderWidth: 1,
        },
    ];

    return {
        labels,
        datasets,
        options: CHART_OPTIONS,
        granularityOptions: getGranularityOptions(),
    };
};

export const useDispensationInsights = () => {
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [preFilters, setPreFilters] = useState([]);
    const [options, setOptions] = useState({
        organization_branches: [],
        pharmacists: [],
        batchs: [],
    });
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [summary, setSummary] = useState(DEFAULT_SUMMARY);
    const [chartRawData, setChartRawData] = useState(DEFAULT_CHART_RAW);

    const filterPayload = useMemo(() => {
        try {
            return JSON.stringify(preFilters && preFilters.length > 0 ? preFilters : []);
        } catch (error) {
            return "[]";
        }
    }, [preFilters]);

    const encodedFilters = useMemo(
        () => encodeURIComponent(filterPayload),
        [filterPayload]
    );

    const chartData = useMemo(
        () => transformChartData(chartRawData),
        [chartRawData]
    );

    const loadOptions = useCallback(async () => {
        setLoadingOptions(true);
        try {
            const response = await fetchData("/dispensing/insights/options", "GET", true);
            const data = response?.data?.data;
            if (data) {
                setOptions({
                    organization_branches: data.organization_branches ?? [],
                    pharmacists: data.pharmacists ?? [],
                    batchs: data.batchs ?? [],
                });
            }
        } catch (error) {
            // silently fail
        } finally {
            setLoadingOptions(false);
        }
    }, []);

    const loadSummary = useCallback(async () => {
        try {
            const response = await fetchData(
                `/dispensing/insights/summary?filter=${encodedFilters}`,
                "GET",
                true
            );
            setSummary(response?.data?.data ?? DEFAULT_SUMMARY);
        } catch (error) {
            setSummary(DEFAULT_SUMMARY);
        }
    }, [encodedFilters]);

    const loadChart = useCallback(async () => {
        try {
            const response = await fetchData(
                `/dispensing/insights/chart?granularity=${filters.granularity}&filter=${encodedFilters}`,
                "GET",
                true
            );
            setChartRawData(response?.data?.data ?? { ...DEFAULT_CHART_RAW, granularity: filters.granularity });
        } catch (error) {
            setChartRawData({ ...DEFAULT_CHART_RAW, granularity: filters.granularity });
        }
    }, [encodedFilters, filters.granularity]);

    useEffect(() => {
        loadOptions();
        loadSummary();
        loadChart();
    }, [loadOptions, loadSummary, loadChart]);

    const handleApplyFilters = useCallback(() => {
        setPreFilters(buildPreFilters(filters));
    }, [filters]);

    const handleClearFilters = useCallback(() => {
        const nextFilters = {
            ...DEFAULT_FILTERS,
            granularity: filters.granularity,
        };
        setFilters(nextFilters);
        setPreFilters([]);
    }, [filters.granularity]);

    const handleRefreshAll = useCallback(() => {
        loadSummary();
        loadChart();
    }, [loadSummary, loadChart]);

    const handleDownload = useCallback(() => {
        downloadUrl(
            `/dispensing/insights/export?order_by=divergence_rate DESC&filter=${encodedFilters}`,
            true
        );
    }, [encodedFilters]);

    return {
        state: {
            filters,
            preFilters,
            options,
            loadingOptions,
            summary,
            chartData
        },
        handlers: {
            setFilters,
            setPreFilters,
            handleApplyFilters,
            handleClearFilters,
            handleRefreshAll,
            handleDownload
        }
    }
};
